import React, { useState } from "react";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Textarea } from "~/components/ui/textarea";
import { Label } from "~/components/ui/label";
import { Dialog, DialogContent } from "~/components/ui/dialog";
import { ArrowLeft, Send } from "lucide-react";
import { sendMessage } from "~/lib/functions/messaging";
import { coachQueryOptions } from "~/lib/query-options";
import { AuthModal } from "~/components/auth-modal";
import { useAuth } from "@clerk/tanstack-react-start";

const contactFormSchema = z.object({
  subject: z
    .string()
    .min(1, "Subject is required")
    .max(200, "Subject must be 200 characters or less"),
  content: z
    .string()
    .min(1, "Message is required")
    .max(5000, "Message must be 5000 characters or less"),
});

type ContactFormData = z.infer<typeof contactFormSchema>;

export const Route = createFileRoute("/$username/contact/")({
  loader: async ({ context, params }) => {
    const coach = await context.queryClient.ensureQueryData(
      coachQueryOptions.byUsername(params.username),
    );

    if (!coach) {
      throw new Error("Coach not found");
    }

    // Check if coach has messaging enabled
    if (coach.profile?.contactMode !== "messaging") {
      throw new Error("This coach does not accept messages");
    }

    return { coach };
  },
  head: ({ params }) => ({
    title: `Contact ${params.username} - Dream Coach Match`,
    meta: [
      {
        name: "description",
        content: `Send a message to ${params.username} on Dream Coach Match`,
      },
    ],
  }),
  component: ContactPage,
});

function ContactPage() {
  const { coach } = Route.useLoaderData();
  const router = useRouter();
  const { isSignedIn } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      subject: "",
      content: "",
    },
  });

  const sendMessageMutation = useMutation({
    mutationFn: (data: ContactFormData) =>
      sendMessage({
        data: {
          coachId: coach.id,
          subject: data.subject,
          content: data.content,
        },
      }),
    onSuccess: (result) => {
      toast.success("Message sent successfully!");
      reset();
      // Redirect to messages page to see the conversation
      router.navigate({
        to: "/messages/$conversationId",
        params: { conversationId: result.conversationId },
      });
    },
    onError: (error) => {
      console.error("Error sending message:", error);
      toast.error("Failed to send message. Please try again.");
    },
  });

  const [pendingFormData, setPendingFormData] =
    useState<ContactFormData | null>(null);

  const onSubmit = (data: ContactFormData) => {
    if (!isSignedIn) {
      setPendingFormData(data);
      setShowAuthModal(true);
      return;
    }
    sendMessageMutation.mutate(data);
  };

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    // The user is now authenticated, submit the pending form data
    if (pendingFormData) {
      // Add a small delay to ensure user creation is complete
      setTimeout(() => {
        sendMessageMutation.mutate(pendingFormData);
        setPendingFormData(null);
      }, 100);
    }
  };

  const coachFullName = `${coach.firstName} ${coach.lastName}`;

  return (
    <div className="mx-auto max-w-2xl px-4 py-8">
      <div className="mb-6">
        <Link
          to="/$username"
          params={{ username: coach.username }}
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to {coachFullName}'s Profile
        </Link>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Send className="mr-2 h-5 w-5" />
            Contact {coachFullName}
          </CardTitle>
          <p className="text-sm text-gray-600">
            Send a message to {coach.firstName} and they'll receive an email
            notification.{" "}
            {!isSignedIn && "You'll need to create an account first, then "}
            You'll be able to continue the conversation in your messages
            dashboard.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <Label htmlFor="subject">Subject</Label>
              <Input
                id="subject"
                {...register("subject")}
                placeholder="What would you like to discuss?"
                disabled={sendMessageMutation.isPending}
                className="mt-1"
              />
              {errors.subject && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.subject.message}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="content">Message</Label>
              <Textarea
                id="content"
                {...register("content")}
                placeholder="Tell them about your goals, what you're looking for in a coach, or any specific questions you have..."
                rows={6}
                disabled={sendMessageMutation.isPending}
                className="mt-1"
              />
              {errors.content && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.content.message}
                </p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                Be specific about your goals and what you're hoping to achieve
                with coaching.
              </p>
            </div>

            <div className="flex items-center justify-end space-x-3">
              <Link to="/$username" params={{ username: coach.username }}>
                <Button variant="outline" type="button">
                  Cancel
                </Button>
              </Link>
              <Button
                type="submit"
                disabled={sendMessageMutation.isPending}
                className="min-w-[120px]"
              >
                {sendMessageMutation.isPending ? (
                  <>
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    {isSignedIn ? "Send Message" : "Sign Up & Send"}
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <div className="mt-6 rounded-lg bg-blue-50 p-4">
        <h3 className="font-medium text-blue-900">What happens next?</h3>
        <ul className="mt-2 space-y-1 text-sm text-blue-800">
          <li>
            • {coach.firstName} will receive an email notification about your
            message
          </li>
          <li>
            • You'll be redirected to your messages where you can continue the
            conversation
          </li>
          <li>• You'll get an email when {coach.firstName} replies</li>
          <li>• All your conversations are saved in your messages dashboard</li>
        </ul>
      </div>

      {/* Auth Modal */}
      <Dialog open={showAuthModal} onOpenChange={setShowAuthModal}>
        <DialogContent className="max-w-lg w-full p-0">
          <AuthModal
            isModalView={true}
            initialMode="signup"
            onSuccess={handleAuthSuccess}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
